import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { AddressProvider } from './contexts/AddressContext';
import { LocationPermissionBanner } from './components/common/LocationPermissionBanner';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import CustomerLogin from './pages/CustomerLogin';
import StaffLogin from './pages/StaffLogin';
import Products from './pages/customer/products';
import ProductDetail from './pages/customer/ProductDetail';
import ProfilePage from './pages/Profile';
import Cart from './pages/customer/Cart';
import Checkout from './pages/customer/Checkout';
import SettingsLayout from './components/settings/SettingsLayout';
import ProfileSettings from './pages/Settings/ProfileSettings';
import SecuritySettings from './pages/Settings/SecuritySettings';
import NotificationsSettings from './pages/Settings/NotificationSettings';
import PreferencesSettings from './pages/Settings/PreferenceSettings';
import Signup from './pages/Signup';
import MyOrders from './pages/customer/MyOrders';
import OrderDetails from './pages/customer/OrderDetails';
import { OrdersProvider } from './contexts/OrdersContext';
import OrderConfirmation from './pages/customer/OrderConfirmation';
import SearchResults from './pages/customer/SearchResults';
import CustomerLayout from './components/layout/CustomerLayout';

// Admin Routes
import AdminRoutes from './routes/AdminRoutes';

// Vendor Routes
import VendorRoutes from './routes/VendorRoutes'

// Driver Routes
import DriverRoutes from './routes/DriverRoutes';
import VendorApply from './pages/Vendor/Apply';
import ApplicationStatus from './pages/Vendor/ApplicationStatus';
import ReturnsList from './pages/customer/Returns';
import ReturnDetail from './pages/customer/ReturnDetail';


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Helper function to check if user has any of the required roles
const hasRequiredRole = (userRoles: string[], requiredRoles: string[]): boolean => {
  if (requiredRoles.length === 0) return true; // No role requirement
  return userRoles.some(role => requiredRoles.includes(role));
};


// Public route wrapper - redirects to home if already logged in
const PublicRoute: React.FC<{
  children: React.ReactNode;
  restricted?: boolean; // If true, redirect to home when logged in (for login pages)
}> = ({ children, restricted = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If restricted route (login) and user is already logged in, redirect to home
  if (restricted && user) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Helper to determine default route based on roles
const getDefaultRoute = (userRoles: string[]) => {
  if (!userRoles || userRoles.length === 0) return '/products';

  // Staff roles → dashboard
  if (userRoles.some(role => ['admin', 'manager', 'staff', 'warehouse'].includes(role))) {
    return '/dashboard';
  }

  // Driver → driver dashboard  
  if (userRoles.includes('driver')) {
    return '/driver';
  }

  // Customer → products
  return '/products';
};

// Default redirect component that checks user roles
const DefaultRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/products" replace />;
  }

  const defaultRoute = getDefaultRoute(user.roles);
  return <Navigate to={defaultRoute} replace />;
};

const App: React.FC = () => {
  const [showLocationBanner, setShowLocationBanner] = useState(true);

  return (
    <GoogleOAuthProvider clientId="739887525813-53v5jp10ba9mg92gf6e88skqh7g0k7ce.apps.googleusercontent.com">
      <QueryClientProvider client={queryClient}>
        <Toaster
          // position="bottom-right"
          toastOptions={{
            duration: 4000,
            success: {
              style: {
                background: '#10B981',
                color: 'white',
              },
            },
            error: {
              style: {
                background: '#EF4444',
                color: 'white',
              },
            },
          }}
        />
        <Router>
          <AuthProvider>
            <AddressProvider>
              <OrdersProvider>
                <CartProvider>
                  {showLocationBanner && <LocationPermissionBanner onClose={() => setShowLocationBanner(false)} />}
                  <Routes>
                    {/* Public Routes - no layout */}
                    <Route path="/login" element={
                      <PublicRoute restricted>
                        <CustomerLogin />
                      </PublicRoute>
                    } />
                    <Route path="/staff/login" element={
                      <PublicRoute restricted>
                        <StaffLogin />
                      </PublicRoute>
                    } />
                    <Route path="/signup" element={
                      <PublicRoute restricted>
                        <Signup />
                      </PublicRoute>
                    } />

                    {/* Vendor Routes - separate layout MUST come BEFORE customer routes*/}
                    <Route path="/vendor/*" element={
                      <ProtectedRoute requiresVendor>
                        <VendorRoutes />
                      </ProtectedRoute>
                    } />

                    {/* Customer Routes with Layout */}
                    <Route element={
                      <CustomerLayout>
                        <Outlet />
                      </CustomerLayout>
                    }>
                      {/* Product browsing - accessible to all */}
                      <Route path="/" element={<Navigate to="/products" replace />} />
                      <Route path="/products" element={
                        <ProtectedRoute allowGuest>
                          <Products />
                        </ProtectedRoute>
                      } />
                      <Route path="/product/:id" element={
                        <ProtectedRoute allowGuest>
                          <ProductDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/search" element={
                        <ProtectedRoute allowGuest>
                          <SearchResults />
                        </ProtectedRoute>
                      } />
                      <Route path="/cart" element={
                        <ProtectedRoute allowGuest>
                          <Cart />
                        </ProtectedRoute>
                      } />
                      {/* Vendor Routes */}
                      <Route path="/vendor/apply" element={<ProtectedRoute><VendorApply /></ProtectedRoute>} />
                      <Route path="/vendor/application-status" element={<ProtectedRoute><ApplicationStatus /></ProtectedRoute>} />
                      {/* <Route path="/vendor/dashboard" element={<ProtectedRoute requiresVendor><VendorDashboard /></ProtectedRoute>} /> */}
                      
                      {/* Customer protected routes */}
                      <Route path="/checkout" element={
                        <ProtectedRoute>
                          <Checkout />
                        </ProtectedRoute>
                      } />
                      <Route path="/orders" element={
                        <ProtectedRoute>
                          <MyOrders />
                        </ProtectedRoute>
                      } />
                      <Route path="/orders/:orderNumber" element={
                        <ProtectedRoute>
                          <OrderDetails />
                        </ProtectedRoute>
                      } />
                      <Route path="/order-confirmation/:orderNumber" element={
                        <ProtectedRoute>
                          <OrderConfirmation />
                        </ProtectedRoute>
                      } />

                      <Route path="returns" element={<ReturnsList />} />
                      <Route path="returns/:id" element={<ReturnDetail />} />
                      {/* User Profile & Settings */}
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/settings" element={
                        <ProtectedRoute>
                          <SettingsLayout />
                        </ProtectedRoute>
                      }>
                        <Route index element={<ProfileSettings />} />
                        <Route path="profile" element={<ProfileSettings />} />
                        <Route path="security" element={<SecuritySettings />} />
                        <Route path="notifications" element={<NotificationsSettings />} />
                        <Route path="preferences" element={<PreferencesSettings />} />
                      </Route>
                    </Route>

                    {/* Admin Routes - separate layout */}
                    <Route path="/dashboard/*" element={
                      <ProtectedRoute allowedRoles={['admin', 'manager', 'staff']}>
                        <AdminRoutes />
                      </ProtectedRoute>
                    } />

                    {/* Driver Routes - separate layout */}
                    <Route path="/driver/*" element={
                      <ProtectedRoute allowedRoles={['driver']}>
                        <DriverRoutes />
                      </ProtectedRoute>
                    } />

                    {/* Default redirect */}
                    <Route path="/" element={<DefaultRedirect />} />

                    {/* 404 */}
                    <Route path="*" element={
                      <div className="min-h-screen flex items-center justify-center">
                        <div className="text-center">
                          <h1 className="text-4xl font-bold text-gray-900">404</h1>
                          <p className="mt-2 text-gray-600">Page not found</p>
                        </div>
                      </div>
                    } />
                  </Routes>
                </CartProvider>
              </OrdersProvider>
            </AddressProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
};

export default App;